
import Swiper from "swiper";
import { Navigation } from "swiper/modules";

import "swiper/css";

export default function StickyScrollNav() {
  const header: HTMLElement | null = document.querySelector("header");
  const stickyScrollNav: HTMLDivElement | null = document.querySelector(".js-scroll-nav");
  const megaMenuContainer = document.querySelector<HTMLElement>(".js-mega-menu-container");
  const hideOnScroll = megaMenuContainer?.getAttribute("data-hide-on-scroll")?.toLowerCase() === "true";

  const handleProgressBarScrolling = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = (scrollTop / docHeight) * 100;

    return scrolled + "%";
  };

  if (stickyScrollNav && header) {
    let isHidden = false; // Track if element is currently hidden (shared state)

    // Check initial state based on current styles
    const checkInitialState = () => {
      const currentPosition = stickyScrollNav.style.position || getComputedStyle(stickyScrollNav).position;
      const currentTop = stickyScrollNav.style.top || getComputedStyle(stickyScrollNav).top;
      isHidden = currentPosition === 'absolute' || currentTop === '-130px' || stickyScrollNav.hasAttribute('inert');
    };

    const getHeaderHeight = () => Math.ceil(header.getBoundingClientRect().height);

    const applyStickyTop = () => {
      if (isHidden) {
        stickyScrollNav.style.setProperty("position", "absolute");
        stickyScrollNav.style.setProperty("top", "-130px");
        return;
      }

      stickyScrollNav.style.setProperty("position", "sticky");
      stickyScrollNav.style.setProperty("top", `${getHeaderHeight()}px`);
    };

    const handleInit = () => {
      const progressBar: HTMLDivElement | null = stickyScrollNav.querySelector(".js-progress-bar");

      applyStickyTop();

      if (progressBar) {
        window.addEventListener("scroll", () => {
          progressBar.style.width = handleProgressBarScrolling();
        });
      }
    };

    checkInitialState();
    handleInit();

    window.addEventListener("resize", applyStickyTop);
    window.addEventListener("scroll", applyStickyTop, { passive: true });

    if (typeof ResizeObserver !== "undefined") {
      const headerResizeObserver = new ResizeObserver(() => {
        applyStickyTop();
      });
      headerResizeObserver.observe(header);
    }

    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(applyStickyTop).observe(header, {
        attributes: true,
        attributeFilter: ["data-issticky", "data-isSticky"],
      });
    }

    if (hideOnScroll) {
      let lastScrollY = window.scrollY;

      window.addEventListener("scroll", () => {
        const currentScrollY = window.scrollY;
        if (currentScrollY > lastScrollY) {
          // User is scrolling down
          isHidden = true;
          applyStickyTop();
          stickyScrollNav.setAttribute('inert', 'true');
        } else if (currentScrollY < lastScrollY) {
          // User is scrolling up
          isHidden = false;
          applyStickyTop();
          stickyScrollNav.removeAttribute('inert');
        }
        lastScrollY = currentScrollY;
      });
    }
  }

  const swiperElement = document.querySelectorAll<HTMLDivElement>(".js-scroll-nav");

  if (swiperElement && swiperElement.length > 0) {
    Swiper.use([Navigation]);

    swiperElement.forEach((el: HTMLDivElement) => {
      if (el) {
        const wrapper = el.querySelector<HTMLDivElement>(".js-wrapper");
        const carouselEl = el.querySelector<HTMLDivElement>(".swiper");
        const slides = el.querySelectorAll<HTMLDivElement>(".swiper-slide");
        const nextBtn = el.querySelector<HTMLButtonElement>(".swiper-btn-next");
        const prevBtn = el.querySelector<HTMLButtonElement>(".swiper-btn-prev");
        const getInitialSlideIndexFromActiveState = () => {
          const activeSlideElement = el.querySelector<HTMLDivElement>(".swiper-slide[data-active='true']");
          if (!activeSlideElement) return 0;

          const activeSlideIndex = Array.from(slides).indexOf(activeSlideElement);
          return activeSlideIndex >= 0 ? activeSlideIndex : 0;
        };

        // Cache original server-rendered active states
        slides.forEach(slide => {
          const isActive = slide.getAttribute("data-active") === "true";
          slide.setAttribute("data-original-active", isActive ? "true" : "false");
        });

        const restoreOriginalState = () => {
          slides.forEach(slide => {
            const isOriginal = slide.getAttribute("data-original-active") === "true";
            slide.setAttribute("data-active", isOriginal ? "true" : "false");
            const link = slide.querySelector("a");
            if (link) {
              if (isOriginal) {
                link.setAttribute("aria-current", "page");
              } else {
                link.removeAttribute("aria-current");
              }
            }
          });
        };

        const syncHashState = () => {
          const currentHash = window.location.hash;
          if (!currentHash) {
            restoreOriginalState();
            return;
          }

          const currentPath = window.location.pathname.replace(/\/+$/, "").toLowerCase();
          let matchFound = false;

          slides.forEach(slide => {
            slide.setAttribute("data-active", "false");
            slide.querySelector("a")?.removeAttribute("aria-current");
          });

          el.querySelectorAll<HTMLAnchorElement>("a").forEach(link => {
            try {
              const url = new URL(link.getAttribute("href") || "", window.location.origin);
              if (url.pathname.replace(/\/+$/, "").toLowerCase() === currentPath && url.hash === currentHash) {
                matchFound = true;
                link.setAttribute("aria-current", "page");
                link.closest(".swiper-slide")?.setAttribute("data-active", "true");
              }
            } catch { }
          });

          if (!matchFound) {
            restoreOriginalState();
          }
        };

        window.addEventListener("hashchange", syncHashState);
        syncHashState();


        const handleTotalWidth = () => {
          let totalWidth = 0;

          if (slides && slides.length > 0) {
            slides.forEach((slide) => {
              totalWidth += slide.offsetWidth;
            });
          }

          return totalWidth;
        };

        const handleShowNavigation = () => {
          const wrapperWidth = wrapper?.offsetWidth || 0;

          if (handleTotalWidth() > wrapperWidth) {
            el.setAttribute("data-show-nav", "true");
          } else {
            el.setAttribute("data-show-nav", "false");
          }
        };

        if (carouselEl) {
          let swiperInstance: any = null;

          const initSwiper = () => {
            if (swiperInstance) {
              swiperInstance.destroy(true, true);
              swiperInstance = null;
            }

            const wrapperWidth = wrapper?.offsetWidth || 0;
            const totalWidth = handleTotalWidth();
            const needsNavigation = totalWidth > wrapperWidth;
            const initialSlideIndexFromActiveState = getInitialSlideIndexFromActiveState();

            swiperInstance = new Swiper(carouselEl, {
              slidesPerView: "auto",
              loop: needsNavigation,
              watchSlidesProgress: true,
              slideToClickedSlide: true,
              initialSlide: initialSlideIndexFromActiveState,
              navigation: {
                nextEl: nextBtn,
                prevEl: prevBtn,
              },
            });

            if (needsNavigation) {
              swiperInstance.slideToLoop(initialSlideIndexFromActiveState, 0);
            } else {
              swiperInstance.slideTo(initialSlideIndexFromActiveState, 0);
            }

            handleShowNavigation();
          };

          const handleResize = () => {
            initSwiper();
          };

          initSwiper();
          window.addEventListener("resize", handleResize);
        }
      }
    });
  }
}
